import React from 'react'
import {
  FiArrowUpRight,
  FiGithub,
  FiMonitor,
  FiServer,
  FiActivity,
  FiRefreshCw,
} from 'react-icons/fi'

export default function Homelab() {
  return (
    <section
      id="homelab"
      aria-labelledby="homelab-title"
      className="scroll-mt-20 border-y border-edge bg-dark-surface py-16 sm:py-24"
    >
      <div className="max-w-5xl mx-auto px-6 sm:px-8">
        <div className="flex items-center gap-4 mb-10">
          <span className="font-mono text-lime text-sm">04</span>
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-cream-dim">
            The Linux homelab
          </p>
          <div className="h-px bg-edge flex-grow" />
        </div>
        <div className="grid md:grid-cols-[1fr_1fr] gap-10 lg:gap-16 items-start">
          <div>
            <h2
              id="homelab-title"
              className="font-display font-bold text-4xl sm:text-5xl tracking-tight leading-[1.05]"
            >
              A small server.
              <br />
              <span className="text-lime">A lot of learning.</span>
            </h2>
            <p className="text-cream-dim leading-relaxed mt-6">
              A homelab is a place to experiment with real IT systems at home.
              Mine is an Ubuntu Linux virtual machine running on my own
              computer. It hosts the digit-recognition app you can try above.
            </p>
            <p className="text-cream-dim leading-relaxed mt-4">
              Writing the app is only part of the work. Here I practise
              deploying it, checking that it’s healthy, and bringing it back
              when something breaks.
            </p>
            <a
              href="https://github.com/SakariaAhmed/Linux-Homelab"
              target="_blank"
              rel="noopener noreferrer"
              className="portfolio-text-link inline-flex items-center gap-2 text-lime text-sm font-medium mt-7"
            >
              <FiGithub aria-hidden="true" /> Explore the lab & learning notes{' '}
              <FiArrowUpRight aria-hidden="true" />
            </a>
          </div>
          <div
            className="lab-diagram rounded-2xl bg-dark border border-edge p-6 sm:p-8"
            aria-label="Your browser reaches the MNIST app on my Ubuntu server through public HTTPS. Monitoring and administration stay private."
          >
            <div className="flex items-center justify-between font-mono text-[10px] uppercase tracking-widest text-cream-dim mb-7">
              <span>From your screen to my server</span>
              <span className="text-lime">↘</span>
            </div>
            <div className="flex items-center gap-3">
              <FiMonitor
                className="text-lime w-5 h-5 shrink-0"
                aria-hidden="true"
              />
              <div>
                <p className="text-cream font-medium text-sm">Your browser</p>
                <p className="text-xs text-cream-dim mt-1">
                  Draw a number. Get a prediction.
                </p>
              </div>
            </div>
            <div className="lab-connector">
              <span>Public HTTPS · Tailscale Funnel</span>
            </div>
            <div className="border border-lime/30 rounded-xl bg-lime/[0.04] p-4 sm:p-5">
              <div className="flex items-center gap-3 mb-5">
                <FiServer
                  className="text-lime w-5 h-5 shrink-0"
                  aria-hidden="true"
                />
                <div>
                  <p className="font-display font-semibold text-cream">
                    My Ubuntu server
                  </p>
                  <p className="text-xs text-cream-dim mt-1">
                    At home. Managed with Terraform.
                  </p>
                </div>
              </div>
              <div className="border border-edge bg-dark rounded-lg px-4 py-3">
                <p className="text-sm text-cream">MNIST application</p>
                <p className="text-xs text-cream-dim mt-1">
                  Docker · FastAPI · TensorFlow
                </p>
              </div>
            </div>
            <div className="lab-connector lab-connector-private">
              <span>Private access only</span>
            </div>
            <div className="flex items-center gap-3">
              <FiActivity
                className="text-cream-dim w-5 h-5 shrink-0"
                aria-hidden="true"
              />
              <div>
                <p className="text-cream font-medium text-sm">
                  Health dashboards & administration
                </p>
                <p className="text-xs text-cream-dim mt-1">
                  Prometheus · Grafana · SSH over Tailscale
                </p>
              </div>
            </div>
          </div>
        </div>
        <div className="grid sm:grid-cols-3 gap-7 sm:gap-8 border-t border-edge mt-12 pt-8">
          {[
            [
              FiServer,
              'Make it repeatable.',
              'Terraform defines the Docker containers, network and storage, so the setup is recorded in code.',
            ],
            [
              FiActivity,
              'See what’s happening.',
              'Prometheus collects metrics. Grafana makes them readable. I tested an outage and watched the alert recover.',
            ],
            [
              FiRefreshCw,
              'Practise recovery.',
              'I tested rolling back a release and restoring a backup onto a fresh virtual machine.',
            ],
          ].map(([Icon, title, body]) => (
            <div key={title}>
              <Icon className="w-5 h-5 text-lime mb-4" aria-hidden="true" />
              <h3 className="font-display font-semibold text-lg mb-2">
                {title}
              </h3>
              <p className="text-sm text-cream-dim leading-relaxed">{body}</p>
            </div>
          ))}
        </div>
        <div className="mt-10 pt-5 border-t border-edge flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-xs">
          <p className="text-cream-dim">
            A single-server learning environment, built to experiment and
            improve.
          </p>
          <p className="font-mono text-lime">
            Next chapter: Azure / studying AZ-104
          </p>
        </div>
      </div>
    </section>
  )
}
